"use client";

import {
  createSiteBuilderBlock,
  createSiteBuilderInsertionBlock,
  siteBuilderFieldValue,
  type SiteBuilderBlock,
  withSiteBuilderFieldValue,
} from "@webdo/site-templates/builder";
import {
  builderStudioFormFields,
  withBuilderStudioFormField,
  withoutBuilderStudioFormField,
} from "./formControls";
import type {
  BuilderStudioBlockInspectorContext,
} from "./types";

export type BuilderInnerBlockInspectorLabels = {
  accordionAdd: string;
  accordionEmpty: string;
  accordionNewText: string;
  accordionNewTitle: string;
  accordionTitle: string;
  chartAddRow: string;
  chartAddSeries: string;
  chartNewRow: (index: number) => string;
  chartNewSeries: (index: number) => string;
  chartRemoveRow: (index: number) => string;
  chartRemoveSeries: (index: number) => string;
  chartRowLabel: (index: number) => string;
  chartValueLabel: (row: string, series: string) => string;
  edit: string;
  fieldLabel: (fieldKey: string) => string;
  formAddField: string;
  formEditField: (label: string) => string;
  formEmpty: string;
  formRemoveField: (label: string) => string;
  formTitle: string;
  removeAccordion: (title: string) => string;
  removeSocial: (label: string) => string;
  socialAdd: string;
  socialDefaultLabel: string;
  socialEmpty: string;
  socialTitle: string;
};

type ChartSeries = {
  borderColor: string;
  color: string;
  label: string;
};

type ChartRow = {
  label: string;
  values: number[];
};

const chartColors = [
  "#18a9bf",
  "#f97316",
  "#8b5cf6",
  "#22c55e",
  "#ef4444",
] as const;

function withSlotItems(
  block: SiteBuilderBlock,
  slot: string,
  items: SiteBuilderBlock[],
): SiteBuilderBlock {
  return {
    ...block,
    slots: {
      ...block.slots,
      [slot]: items,
    },
  };
}

function legacyAccordionItems(block: SiteBuilderBlock): SiteBuilderBlock[] {
  if (!Array.isArray(block.content?.items)) {
    return [];
  }

  return block.content.items.flatMap((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) {
      return [];
    }

    const value = item as Record<string, unknown>;
    const title = typeof value.title === "string"
      ? value.title
      : typeof value.question === "string"
        ? value.question
        : "";
    const text = typeof value.text === "string"
      ? value.text
      : typeof value.answer === "string"
        ? value.answer
        : "";

    return [{
      type: "accordionItem",
      content: { text, title },
    }];
  });
}

function accordionItems(block: SiteBuilderBlock): SiteBuilderBlock[] {
  return Object.prototype.hasOwnProperty.call(block.slots ?? {}, "content")
    ? block.slots?.content ?? []
    : legacyAccordionItems(block);
}

function materializedAccordionItems(block: SiteBuilderBlock): SiteBuilderBlock[] {
  return accordionItems(block).map((item) => {
    if (item.uuid) {
      return item;
    }

    const created = createSiteBuilderBlock("accordionItem");

    return {
      ...created,
      content: {
        ...created.content,
        ...item.content,
      },
      options: item.options,
    };
  });
}

function withAccordionItems(
  block: SiteBuilderBlock,
  items: SiteBuilderBlock[],
): SiteBuilderBlock {
  const content = { ...(block.content ?? {}) };

  delete content.items;

  return {
    ...withSlotItems(block, "content", items),
    content: Object.keys(content).length > 0 ? content : undefined,
  };
}

function panelClassName() {
  return "border-t border-border py-2 first:border-t-0 first:pt-0";
}

function ManagerHeader({
  action,
  actionDisabled = false,
  onAction,
  title,
}: {
  action: string;
  actionDisabled?: boolean;
  onAction: () => void;
  title: string;
}) {
  return (
    <div className="mb-2 flex items-center justify-between gap-2">
      <span className="text-xs font-semibold text-text">{title}</span>
      <button
        type="button"
        className="rounded border border-primary/40 bg-primary/10 px-2 py-1 text-[10px] font-semibold text-primary hover:bg-primary/20 disabled:cursor-not-allowed disabled:opacity-40"
        disabled={actionDisabled}
        onClick={onAction}
      >
        {action}
      </button>
    </div>
  );
}

function FormFieldManager({
  context,
  labels,
}: {
  context: BuilderStudioBlockInspectorContext;
  labels: BuilderInnerBlockInspectorLabels;
}) {
  const fields = builderStudioFormFields(context.block);
  const items = context.block.slots?.items ?? [];
  const capacity = context.definition.zones?.find(
    (zone) => zone.key === "items",
  )?.capacity;
  const capacityReached = typeof capacity === "number"
    && items.length >= capacity;

  return (
    <section
      className={panelClassName()}
      data-builder-form-field-manager="true"
    >
      <ManagerHeader
        action={labels.formAddField}
        actionDisabled={capacityReached}
        onAction={() => {
          const field = context.createNestedBlock?.("field")
            ?? createSiteBuilderInsertionBlock(
              createSiteBuilderBlock("field"),
              { placement: "flow" },
            );
          const nextForm = withBuilderStudioFormField(context.block, field);

          if (nextForm === context.block) {
            return;
          }

          context.updateBlock(nextForm, {
            inspectorCollapsed: false,
            selectBlockUuid: field.uuid ?? null,
          });
        }}
        title={labels.formTitle}
      />
      {fields.length === 0 ? (
        <p className="text-[10px] leading-4 text-text-secondary">
          {labels.formEmpty}
        </p>
      ) : (
        <div className="space-y-1.5">
          {fields.map((field, index) => {
            const authoredLabel = typeof field.content?.label === "string"
              ? field.content.label.trim()
              : "";
            const label = authoredLabel || labels.fieldLabel("label");
            const scopeEntry = context.scopeEntries?.find((entry) => (
              entry.block === field
              || Boolean(field.uuid && entry.block.uuid === field.uuid)
            ));

            return (
              <div
                className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-1 rounded border border-border bg-surface p-1.5"
                data-builder-form-field={field.uuid ?? index}
                key={field.uuid ?? `${label}:${index}`}
              >
                <span className="min-w-0 truncate text-[11px] font-semibold text-text">
                  {label}
                </span>
                <button
                  type="button"
                  aria-label={labels.formEditField(label)}
                  className="rounded px-1.5 py-1 text-[10px] text-primary hover:bg-surface-hover disabled:opacity-35"
                  disabled={!scopeEntry}
                  onClick={() => {
                    if (scopeEntry) {
                      context.onSelectScope?.({
                        ...scopeEntry.selection,
                        part: null,
                      });
                    }
                  }}
                >
                  {labels.edit}
                </button>
                <button
                  type="button"
                  aria-label={labels.formRemoveField(label)}
                  className="rounded px-1.5 py-1 text-[10px] text-danger hover:bg-surface-hover disabled:opacity-35"
                  disabled={!field.uuid}
                  onClick={() => {
                    if (!field.uuid) {
                      return;
                    }

                    context.updateBlock(withoutBuilderStudioFormField(
                      context.block,
                      field.uuid,
                    ));
                  }}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function SocialLinkManager({
  context,
  labels,
}: {
  context: BuilderStudioBlockInspectorContext;
  labels: BuilderInnerBlockInspectorLabels;
}) {
  const items = context.block.slots?.items ?? [];

  return (
    <section
      className={panelClassName()}
      data-builder-social-link-manager="true"
    >
      <ManagerHeader
        action={labels.socialAdd}
        onAction={() => {
          const item = createSiteBuilderBlock("socialLink");

          context.updateBlock(withSlotItems(context.block, "items", [
            ...items,
            {
              ...item,
              content: {
                ...item.content,
                href: "https://",
                icon: "social:link",
                label: labels.socialDefaultLabel,
              },
              options: {
                ...item.options,
                openInNewWindow: "true",
              },
            },
          ]));
        }}
        title={labels.socialTitle}
      />
      {items.length === 0 ? (
        <p className="text-[10px] leading-4 text-text-secondary">
          {labels.socialEmpty}
        </p>
      ) : (
        <div className="space-y-1.5">
          {items.map((item, index) => {
            const label = typeof item.content?.label === "string"
              ? item.content.label
              : labels.socialDefaultLabel;
            const href = typeof item.content?.href === "string"
              ? item.content.href
              : "";
            const scopeEntry = context.scopeEntries?.find((entry) => (
              item.uuid && entry.block.uuid === item.uuid
            ));

            return (
              <div
                className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-1 rounded border border-border bg-surface p-1.5"
                key={item.uuid ?? `${label}:${index}`}
              >
                <span className="min-w-0">
                  <span className="block truncate text-[11px] font-semibold text-text">
                    {label}
                  </span>
                  <span className="block truncate text-[9px] text-text-secondary">
                    {href}
                  </span>
                </span>
                <button
                  type="button"
                  className="rounded px-1.5 py-1 text-[10px] text-primary hover:bg-surface-hover disabled:opacity-35"
                  disabled={!scopeEntry}
                  onClick={() => {
                    if (scopeEntry) {
                      context.onSelectScope?.({
                        ...scopeEntry.selection,
                        part: null,
                      });
                    }
                  }}
                >
                  {labels.edit}
                </button>
                <button
                  type="button"
                  aria-label={labels.removeSocial(label)}
                  className="rounded px-1.5 py-1 text-[10px] text-danger hover:bg-surface-hover"
                  onClick={() => {
                    context.updateBlock(withSlotItems(
                      context.block,
                      "items",
                      items.filter((_, candidate) => candidate !== index),
                    ));
                  }}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function AccordionItemManager({
  context,
  labels,
}: {
  context: BuilderStudioBlockInspectorContext;
  labels: BuilderInnerBlockInspectorLabels;
}) {
  const items = accordionItems(context.block);
  const materializedItems = () => materializedAccordionItems(context.block);

  return (
    <section
      className={panelClassName()}
      data-builder-accordion-item-manager="true"
    >
      <ManagerHeader
        action={labels.accordionAdd}
        onAction={() => {
          const item = createSiteBuilderBlock("accordionItem");

          context.updateBlock(withAccordionItems(context.block, [
            ...materializedItems(),
            {
              ...item,
              content: {
                ...item.content,
                text: labels.accordionNewText,
                title: labels.accordionNewTitle,
              },
            },
          ]));
        }}
        title={labels.accordionTitle}
      />
      {items.length === 0 ? (
        <p className="text-[10px] leading-4 text-text-secondary">
          {labels.accordionEmpty}
        </p>
      ) : (
        <div className="space-y-1.5">
          {items.map((item, index) => {
            const title = typeof item.content?.title === "string"
              ? item.content.title
              : labels.accordionNewTitle;
            const scopeEntry = context.scopeEntries?.find((entry) => (
              item.uuid && entry.block.uuid === item.uuid
            ));

            return (
              <div
                className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-1 rounded border border-border bg-surface p-1.5"
                key={item.uuid ?? `${title}:${index}`}
              >
                <span className="truncate text-[11px] font-semibold text-text">
                  {title}
                </span>
                <button
                  type="button"
                  className="rounded px-1.5 py-1 text-[10px] text-primary hover:bg-surface-hover disabled:opacity-35"
                  disabled={!scopeEntry}
                  onClick={() => {
                    if (scopeEntry) {
                      context.onSelectScope?.({
                        ...scopeEntry.selection,
                        part: null,
                      });
                    }
                  }}
                >
                  {labels.edit}
                </button>
                <button
                  type="button"
                  aria-label={labels.removeAccordion(title)}
                  className="rounded px-1.5 py-1 text-[10px] text-danger hover:bg-surface-hover"
                  onClick={() => {
                    context.updateBlock(withAccordionItems(
                      context.block,
                      materializedItems().filter(
                        (_, candidate) => candidate !== index,
                      ),
                    ));
                  }}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function parsedSeries(block: SiteBuilderBlock): ChartSeries[] {
  try {
    const parsed = JSON.parse(String(block.content?.chartSeries ?? "[]"));

    if (Array.isArray(parsed)) {
      const values = parsed.flatMap((item, index) => (
        item && typeof item === "object"
          ? [{
            borderColor: String(item.borderColor ?? item.color ?? chartColors[
              index % chartColors.length
            ]),
            color: String(item.color ?? chartColors[index % chartColors.length]),
            label: String(item.label ?? ""),
          }]
          : []
      ));

      if (values.length > 0) {
        return values;
      }
    }
  } catch {
    // Invalid drafts stay editable through a safe default dataset.
  }

  return [{
    borderColor: "#0f8fa3",
    color: "#18a9bf",
    label: "1",
  }];
}

function parsedRows(block: SiteBuilderBlock, seriesCount: number): ChartRow[] {
  try {
    const parsed = JSON.parse(String(block.content?.chartRows ?? "[]"));

    if (Array.isArray(parsed)) {
      const rows = parsed.flatMap((item) => (
        item && typeof item === "object"
          ? [{
            label: String(item.label ?? ""),
            values: Array.from(
              { length: seriesCount },
              (_, index) => {
                const value = Number(
                  Array.isArray(item.values) ? item.values[index] : 0,
                );

                return Number.isFinite(value) ? value : 0;
              },
            ),
          }]
          : []
      ));

      if (rows.length > 0) {
        return rows;
      }
    }
  } catch {
    // Invalid drafts stay editable through a safe default dataset.
  }

  return [{
    label: "1",
    values: Array.from({ length: seriesCount }, () => 0),
  }];
}

function ChartEditor({
  context,
  labels,
}: {
  context: BuilderStudioBlockInspectorContext;
  labels: BuilderInnerBlockInspectorLabels;
}) {
  const series = parsedSeries(context.block);
  const rows = parsedRows(context.block, series.length);
  const inputClassName = "h-8 min-w-0 rounded border border-border bg-surface px-2 text-xs text-text outline-none focus:border-primary";
  const updateData = (
    nextSeries: ChartSeries[],
    nextRows: ChartRow[],
  ) => {
    context.updateBlock({
      ...context.block,
      content: {
        ...context.block.content,
        chartRows: JSON.stringify(nextRows),
        chartSeries: JSON.stringify(nextSeries),
      },
    });
  };

  return (
    <section
      className={panelClassName()}
      data-builder-chart-editor="true"
    >
      <ManagerHeader
        action={labels.chartAddSeries}
        onAction={() => {
          const index = series.length;
          const color = chartColors[index % chartColors.length] ?? "#18a9bf";

          updateData(
            [...series, {
              borderColor: color,
              color,
              label: labels.chartNewSeries(index + 1),
            }],
            rows.map((row) => ({
              ...row,
              values: [...row.values, 0],
            })),
          );
        }}
        title={labels.fieldLabel("chartSeries")}
      />
      <div className="space-y-1.5">
        {series.map((item, index) => (
          <div
            className="grid grid-cols-[32px_minmax(0,1fr)_28px] gap-1"
            key={`series:${index}`}
          >
            <input
              type="color"
              aria-label={labels.fieldLabel("datasetColor")}
              className="h-8 w-8 rounded border border-border bg-transparent p-1"
              value={/^#[\da-f]{6}$/iu.test(item.color)
                ? item.color
                : "#18a9bf"}
              onChange={(event) => updateData(
                series.map((candidate, candidateIndex) => (
                  candidateIndex === index
                    ? {
                      ...candidate,
                      borderColor: event.target.value,
                      color: event.target.value,
                    }
                    : candidate
                )),
                rows,
              )}
            />
            <input
              aria-label={labels.chartNewSeries(index + 1)}
              className={inputClassName}
              value={item.label}
              onChange={(event) => updateData(
                series.map((candidate, candidateIndex) => (
                  candidateIndex === index
                    ? { ...candidate, label: event.target.value }
                    : candidate
                )),
                rows,
              )}
            />
            <button
              type="button"
              aria-label={labels.chartRemoveSeries(index + 1)}
              className="rounded bg-surface text-danger disabled:opacity-30"
              disabled={series.length <= 1}
              onClick={() => updateData(
                series.filter((_, candidate) => candidate !== index),
                rows.map((row) => ({
                  ...row,
                  values: row.values.filter(
                    (_, candidate) => candidate !== index,
                  ),
                })),
              )}
            >
              −
            </button>
          </div>
        ))}
      </div>
      <div className="mt-3 overflow-x-auto">
        <div
          className="grid min-w-max gap-1"
          style={{
            gridTemplateColumns: `minmax(90px,1fr) repeat(${series.length}, 72px) 28px`,
          }}
        >
          <span />
          {series.map((item, index) => (
            <span
              className="truncate text-[10px] text-text-secondary"
              key={`heading:${index}`}
            >
              {item.label}
            </span>
          ))}
          <span />
          {rows.map((row, rowIndex) => (
            <div className="contents" key={`row:${rowIndex}`}>
              <input
                aria-label={labels.chartRowLabel(rowIndex + 1)}
                className={inputClassName}
                value={row.label}
                onChange={(event) => updateData(
                  series,
                  rows.map((candidate, candidateIndex) => (
                    candidateIndex === rowIndex
                      ? { ...candidate, label: event.target.value }
                      : candidate
                  )),
                )}
              />
              {series.map((seriesItem, seriesIndex) => (
                <input
                  type="number"
                  aria-label={labels.chartValueLabel(
                    row.label,
                    seriesItem.label,
                  )}
                  className={`${inputClassName} text-right`}
                  key={`value:${seriesIndex}`}
                  value={row.values[seriesIndex] ?? 0}
                  onChange={(event) => updateData(
                    series,
                    rows.map((candidate, candidateIndex) => (
                      candidateIndex === rowIndex
                        ? {
                          ...candidate,
                          values: candidate.values.map(
                            (value, valueIndex) => (
                              valueIndex === seriesIndex
                                ? event.target.valueAsNumber
                                : value
                            ),
                          ),
                        }
                        : candidate
                    )),
                  )}
                />
              ))}
              <button
                type="button"
                aria-label={labels.chartRemoveRow(rowIndex + 1)}
                className="rounded bg-surface text-danger disabled:opacity-30"
                disabled={rows.length <= 1}
                onClick={() => updateData(
                  series,
                  rows.filter((_, candidate) => candidate !== rowIndex),
                )}
              >
                −
              </button>
            </div>
          ))}
        </div>
      </div>
      <button
        type="button"
        className="mt-2 w-full rounded border border-primary/40 bg-primary/10 px-2 py-1.5 text-[10px] font-semibold text-primary"
        onClick={() => updateData(
          series,
          [...rows, {
            label: labels.chartNewRow(rows.length + 1),
            values: Array.from({ length: series.length }, () => 0),
          }],
        )}
      >
        {labels.chartAddRow}
      </button>
    </section>
  );
}

function RatingEditor({
  context,
  labels,
}: {
  context: BuilderStudioBlockInspectorContext;
  labels: BuilderInnerBlockInspectorLabels;
}) {
  const maxValue = Number(context.block.content?.ratingMax);
  const max = Number.isFinite(maxValue) && maxValue > 0 ? maxValue : 5;
  const ratingValue = Number(siteBuilderFieldValue(
    context.block,
    "content",
    "ratingValue",
  ));
  const value = Number.isFinite(ratingValue)
    ? Math.max(0, Math.min(max, ratingValue))
    : 0;
  const update = (key: string, nextValue: string) => {
    context.updateBlock(withSiteBuilderFieldValue(
      context.block,
      "content",
      key,
      nextValue,
    ));
  };

  return (
    <section
      className={panelClassName()}
      data-builder-rating-editor="true"
    >
      <h4 className="mb-2 text-xs font-semibold text-text">
        {labels.fieldLabel("ratingValue")}
      </h4>
      <input
        type="range"
        aria-label={labels.fieldLabel("ratingValue")}
        className="w-full accent-primary"
        max={max}
        min={0}
        step={0.1}
        value={value}
        onChange={(event) => update("ratingValue", event.target.value)}
      />
      <div className="mt-2 grid grid-cols-[minmax(0,1fr)_auto_80px] items-center gap-2">
        <input
          type="number"
          aria-label={labels.fieldLabel("ratingValue")}
          className="h-8 min-w-0 rounded border border-border bg-surface px-2 text-right text-xs text-text"
          max={max}
          min={0}
          step={0.1}
          value={value}
          onChange={(event) => update("ratingValue", event.target.value)}
        />
        <span className="text-text-secondary">/</span>
        <input
          type="number"
          aria-label={labels.fieldLabel("ratingMax")}
          className="h-8 min-w-0 rounded border border-border bg-surface px-2 text-right text-xs text-text"
          min={1}
          step={1}
          value={max}
          onChange={(event) => update("ratingMax", event.target.value)}
        />
      </div>
    </section>
  );
}

export function BuilderInnerBlockSpecializedPanels({
  context,
  labels,
}: {
  context: BuilderStudioBlockInspectorContext;
  labels: BuilderInnerBlockInspectorLabels;
}) {
  if (context.block.type === "form") {
    return <FormFieldManager context={context} labels={labels} />;
  }

  if (context.block.type === "socialMedia") {
    return <SocialLinkManager context={context} labels={labels} />;
  }

  if (context.block.type === "accordion") {
    return <AccordionItemManager context={context} labels={labels} />;
  }

  if (context.block.type === "chart") {
    return <ChartEditor context={context} labels={labels} />;
  }

  if (context.block.type === "rating") {
    return <RatingEditor context={context} labels={labels} />;
  }

  return null;
}

export const BUILDER_SPECIALIZED_INNER_BLOCK_TYPES = [
  "accordion",
  "chart",
  "form",
  "rating",
  "socialMedia",
] as const;
